import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Chips } from 'primereact/chips'
import { Dialog } from 'primereact/dialog'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { OverlayPanel } from 'primereact/overlaypanel'
import { classNames } from 'primereact/utils'

import { ActiveBodyTemplate } from '../../../../../../components/tableTemplates'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { SupplierDetail } from '../../../../supplier/data'
import { Supplier, SupplierList } from '../../../../suppliers/data'
import { RoleSet, RoleSets } from '../../../data'
import { FormFieldName } from './AddAssignee.container'

type Props = {
  formik: FormikProps<any>
  isFormFieldInvalid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => JSX.Element
  config: any
  roleSets?: RoleSets
  suppliers?: SupplierList
  wrongEmails: string[] | null
  projectRoles?: (string | undefined)[]
  tenantRoles?: (string | undefined)[]
  visible: boolean
  onHide: () => void
}

export const AddAssignee: React.FC<Props> = ({
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
  config,
  roleSets,
  suppliers,
  projectRoles,
  wrongEmails,
  tenantRoles,
  visible,
  onHide,
}) => {
  const { t } = useTranslation()

  const [permissionDescription, setPermissionDescription] = useState(<></>)

  const op = useRef<OverlayPanel>(null)

  const errorStyle = {
    margin: '-0.25rem -0.5rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '16px',
    paddingRight: '30px',
    marginRight: '-30px',
  }

  const renderCustomChip = (item: string) => {
    const isInvalidEmail = wrongEmails?.includes(item)

    if (isInvalidEmail) {
      return (
        <div
          className="block text-red-500 border-1 border-red-500 bg-red-50"
          style={errorStyle}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '14px' }}
          ></i>
          <span className="ml-2">{item}</span>
        </div>
      )
    }

    return item
  }

  // Dialog components
  // Assignee
  const assigneeLabel = (
    <label
      htmlFor="assignee"
      className={classNames('text-sm', {
        'p-error': isFormFieldInvalid('assignee'),
      })}
    ></label>
  )

  const assigneeComponent = (
    <Chips
      id="assignee"
      name="assignee"
      value={formik.values.assignee}
      separator=";"
      onChange={formik.handleChange}
      className="block"
      itemTemplate={renderCustomChip}
      addOnBlur={true}
      allowDuplicate={false}
      pt={{
        container: { className: 'pb-2' },
        token: { className: 'mt-2' },
      }}
    />
  )

  const assigneeErrorMsg = <div>{getFormErrorMessage('assignee')}</div>

  const assigneeWrapper = (
    <ConfigurableComponent
      config={config}
      component={assigneeComponent}
      label={assigneeLabel}
      errorMsg={assigneeErrorMsg}
    />
  )

  // Project
  const projectLabel = (
    <label
      htmlFor="project"
      className={classNames('text-sm', {
        'p-error': isFormFieldInvalid('project'),
      })}
    ></label>
  )

  const projectValueTemplate = (option: SupplierDetail, props: any) => {
    if (option) {
      return <span>{option.Name}</span>
    }
    return <span>{props.placeholder}</span>
  }

  const projectItemTemplate = (option: SupplierDetail) => {
    return (
      <>
        <div className="flex align-items-center">
          <div className="flex flex-column my-1 mr-3">
            <div className="font-semibold">{option.Name}</div>
            <div className="text-sm font-italic">{option.Id}</div>
            <div className="text-sm mt-2">
              {ActiveBodyTemplate(option.Status)}
            </div>
          </div>
          <div className="ml-auto flex align-items-center gap-2">
            {option.Id === formik.values.project && (
              <i className="pi pi-check"></i>
            )}
          </div>
        </div>
      </>
    )
  }

  const projectComponent = (
    <Dropdown
      id="project"
      className={classNames('w-full', {
        'p-invalid': isFormFieldInvalid('project'),
      })}
      value={formik.values.project}
      onChange={(e: DropdownChangeEvent) => {
        formik.handleChange(e)
        !e.value && formik.setFieldValue('role', [])
      }}
      options={suppliers}
      optionLabel="Name"
      optionValue="Id"
      valueTemplate={projectValueTemplate}
      disabled={tenantRoles?.some(x => formik.values.role.includes(x))}
      itemTemplate={projectItemTemplate}
      filter
      filterBy="Name,Id"
      showClear
    />
  )

  const projectErrorMsg = <div>{getFormErrorMessage('project')}</div>

  const projectWrapper = (
    <ConfigurableComponent
      config={config}
      component={projectComponent}
      label={projectLabel}
      errorMsg={projectErrorMsg}
    />
  )

  // // Permission
  // const selectedRole = formik.values.role
  const getPermissions = (option: RoleSet) => {
    const matchingRoleSet = roleSets?.find(
      roleSet => roleSet.role.name === option.role.name,
    )
    let descriptions: string[] | undefined
    if (matchingRoleSet) {
      descriptions = matchingRoleSet.permissions.map(
        permission => permission.description,
      )
    }
    const permissions = (
      <>
        <h5 className="my-0">
          {t('roleAssignments.table.edit.dialog.form.permissions.label')}
        </h5>
        <ul className="mt-0 pl-3 h-7rem overflow-y-auto">
          {descriptions?.map(description => (
            <li key={description} className="text-sm">
              {description}
            </li>
          ))}
        </ul>
      </>
    )
    setPermissionDescription(permissions)
  }

  // Role
  const roleLabel = (
    <label
      htmlFor="role"
      className={classNames('text-sm', {
        'p-error': isFormFieldInvalid('role'),
      })}
    ></label>
  )

  const roleItemTemplate = (option: RoleSet) => {
    return (
      <>
        <div className="flex align-items-center">
          <div className="flex flex-column my-1 mr-3">
            <div>{option.role.name}</div>
            {/* <div className="text-sm font-italic">{option.role.description}</div> */}
          </div>
          {option.role.name === formik.values.role && (
            <div className="ml-auto">
              <i className="pi pi-check"></i>
            </div>
          )}
          <div className="flex flex-column">
            <span
              className="pi pi-info border-circle bg-gray-200 border-none"
              style={{ fontSize: '0.8rem', padding: '5px' }}
              onMouseOverCapture={e => {
                op.current!.toggle(e)
                getPermissions(option)
              }}
            />
            <OverlayPanel ref={op} closeOnEscape>
              {permissionDescription}
            </OverlayPanel>
          </div>
        </div>
      </>
    )
  }

  const roleComponent = (
    <MultiSelect
      id="role"
      className={classNames('w-full', {
        'p-invalid': isFormFieldInvalid('role'),
      })}
      value={formik.values.role}
      onChange={formik.handleChange}
      options={
        formik.values.project
          ? roleSets?.filter(roleSet => roleSet.role.level === 1)
          : roleSets?.filter(roleSet => roleSet.role.level === 0)
      }
      optionValue="role.name"
      optionLabel="role.name"
      itemTemplate={roleItemTemplate}
      filter
      filterBy="role.name"
      selectedItemsLabel={t(
        'roleAssignments.table.add.dialog.form.role.selectedItemsLabel',
        { count: formik.values.role.length },
      )}
      maxSelectedLabels={1}
    />
  )

  const roleErrorMsg = <div>{getFormErrorMessage('role')}</div>

  const roleWrapper = (
    <ConfigurableComponent
      config={config}
      component={roleComponent}
      label={roleLabel}
      errorMsg={roleErrorMsg}
    />
  )

  // Header
  const header = <>{t('roleAssignments.table.add.dialog.title')}</>
  // Footer
  const footer = (
    <>
      <Button
        label={t('roleAssignments.table.add.dialog.cancel.label')}
        icon="pi pi-times"
        className="p-button-text mt-3 w-full md:w-auto mb-1 md:mb-0"
        onClick={onHide}
      />
      <Button
        label={t('roleAssignments.table.add.dialog.add.label')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        disabled={
          formik.values.role.length === 0 ||
          formik.values.assignee.length === 0 ||
          wrongEmails !== null
        }
      />
    </>
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '30rem' }}
        breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
        footer={footer}
        onHide={onHide}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">{assigneeWrapper}</div>
          <div className="col-12">{projectWrapper}</div>
          <div className="col-12">{roleWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
